import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, Phone, MapPin, Flower2, ShieldPlus, HeartHandshake, Sparkles, Play, X, Navigation } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Botanical, LotusDivider } from "@/components/Botanical";
import { tel, ASSETS } from "@/data/site";
import { useContent } from "@/context/ContentContext";
import { useOpenStatus } from "@/hooks/useOpenStatus";

const ROOMS = [
  { src: "/brand/clinic-4.jpg", title: "Front Desk", desc: "Warm wood and gentle tones welcome you in.", note: "Welcome. Our front desk will greet you, confirm your appointment and answer any questions before you settle in." },
  { src: "/brand/clinic-1.jpg", title: "Reception & Lounge", desc: "Soft light, greenery and a calm place to settle in.", note: "Take a seat, take a breath. We keep waiting times short so you're never left anxious before your consultation." },
  { src: "/brand/clinic-2.jpg", title: "Consultation Room", desc: "A private space for unhurried conversations.", note: "This is where we'll talk — no rush, no interruptions. Bring your questions; every one of them matters." },
  { src: "/brand/clinic-3.jpg", title: "Examination Suite", desc: "Designed around dignity, privacy and comfort.", note: "Your privacy and comfort come first here. Everything is explained before it's done, and you're in control throughout." },
];
const TOUR_STEP_MS = 7000;

const PILLARS = [
  { icon: Flower2, t: "Comfort First", d: "Calm, cosy interiors designed for your comfort." },
  { icon: ShieldPlus, t: "Privacy Assured", d: "Thoughtful layout to ensure your privacy." },
  { icon: HeartHandshake, t: "Patient Focused", d: "Every corner created to support your well-being." },
  { icon: Sparkles, t: "Modern & Warm", d: "A perfect blend of modern care and a warm touch." },
];

function Stage({ index, setIndex }) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: -py * 14, y: px * 18 });
  };
  const reset = () => setTilt({ x: 0, y: 0 });
  const prev = () => setIndex((i) => (i - 1 + ROOMS.length) % ROOMS.length);
  const next = () => setIndex((i) => (i + 1) % ROOMS.length);

  return (
    <div className="relative mx-auto max-w-3xl" style={{ perspective: "1400px" }} data-testid="digital-visit-stage">
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        className="relative aspect-[16/10] w-full transition-transform duration-300 ease-out [transform-style:preserve-3d]"
        style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}
      >
        {ROOMS.map((room, i) => {
          const offset = (i - index + ROOMS.length) % ROOMS.length;
          const depth = offset === 0 ? 0 : -140 * offset;
          const shift = offset === 0 ? 0 : (offset % 2 === 0 ? -1 : 1) * 6 * offset;
          return (
            <img
              key={room.src}
              src={room.src}
              alt={room.title}
              data-testid={`digital-visit-slide-${i}`}
              className="absolute inset-0 h-full w-full rounded-[2rem] object-cover shadow-lift ring-1 ring-cream/60 transition-[transform,opacity,filter] duration-700 ease-out"
              style={{
                transform: `translateZ(${depth}px) translateX(${shift}%) translateY(${-offset * 7}%) scale(${1 - offset * 0.06})`,
                opacity: offset > 2 ? 0 : 1 - offset * 0.25,
                filter: offset ? `blur(${offset}px) saturate(0.8)` : "none",
                zIndex: ROOMS.length - offset,
              }}
              loading="lazy"
            />
          );
        })}

        <div
          className="pointer-events-none absolute bottom-6 left-6 z-10 max-w-xs rounded-2xl border border-gold/30 bg-cream/95 p-4 shadow-card backdrop-blur md:bottom-8 md:left-8"
          style={{ transform: "translateZ(70px)" }}
          data-testid="digital-visit-caption"
        >
          <p className="font-serif text-lg text-charcoal">{ROOMS[index].title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-taupe">{ROOMS[index].desc}</p>
        </div>

        <span
          className="pointer-events-none absolute right-6 top-6 z-10 rounded-full bg-terracotta/90 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-cream"
          style={{ transform: "translateZ(90px)" }}
        >
          {index + 1} / {ROOMS.length}
        </span>
      </div>

      <button onClick={prev} aria-label="Previous room" data-testid="digital-visit-prev" className="absolute -left-3 top-1/2 z-20 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-soft-border bg-cream text-terracotta shadow-soft transition-transform hover:scale-105 md:-left-6">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button onClick={next} aria-label="Next room" data-testid="digital-visit-next" className="absolute -right-3 top-1/2 z-20 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-soft-border bg-cream text-terracotta shadow-soft transition-transform hover:scale-105 md:-right-6">
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
}

export default function DigitalVisit({ compact = false }) {
  const CLINIC = useContent();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tour, setTour] = useState(false);
  const status = useOpenStatus();

  useEffect(() => {
    if (paused && !tour) return;
    const t = setInterval(() => {
      setIndex((i) => {
        if (tour && i === ROOMS.length - 1) { setTour(false); return 0; }
        return (i + 1) % ROOMS.length;
      });
    }, tour ? TOUR_STEP_MS : 4500);
    return () => clearInterval(t);
  }, [paused, tour]);

  const startTour = () => { setIndex(0); setTour(true); };
  const endTour = () => setTour(false);
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(CLINIC.address)}`;

  return (
    <section data-testid="digital-visit-section" className="relative overflow-hidden bg-gradient-to-b from-blush via-[#FBE9EA] to-cream py-20 md:py-28">
      <Botanical className="pointer-events-none absolute -left-8 top-10 h-72 w-40 opacity-25" />
      <Botanical className="pointer-events-none absolute -right-8 bottom-10 h-72 w-40 rotate-180 opacity-25" />
      <div className="container-clinic">
        <Reveal className="text-center">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Step Inside</p>
          <h2 className="mt-4 font-serif text-4xl leading-none tracking-tight text-terracotta-dark sm:text-5xl lg:text-6xl">A Digital Visit</h2>
          <p className="mt-1 font-serif text-3xl italic text-terracotta sm:text-4xl">to the Clinic</p>
          <LotusDivider className="mt-5" />
          <span className="mt-6 inline-block rounded-full bg-terracotta px-6 py-2 font-sans text-sm font-medium text-cream">Thoughtfully Designed. Warmly Welcoming.</span>
          <p className="mx-auto mt-4 max-w-xl text-base text-taupe">Step inside {CLINIC.clinicName} — a space built around care, comfort, and you.</p>
          {!tour && (
            <button onClick={startTour} data-testid="tour-start" className="mt-6 inline-flex items-center gap-2 rounded-full border border-terracotta/40 bg-cream px-7 py-3.5 font-sans text-sm font-semibold text-terracotta shadow-soft transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-blush active:scale-95">
              <Play className="h-4 w-4 fill-terracotta" /> Start Guided Tour
            </button>
          )}
        </Reveal>

        <Reveal delay={120} className="mt-14" >
          <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <Stage index={index} setIndex={setIndex} />
          </div>
          {tour && (
            <div data-testid="tour-panel" className="relative mx-auto mt-6 max-w-3xl animate-fade-up rounded-3xl border border-gold/30 bg-cream p-5 shadow-card md:p-6">
              <div className="flex items-start gap-4">
                <img src={ASSETS.doctorSaree} alt="Dr. Mithee" className="h-14 w-14 shrink-0 rounded-full object-cover object-top ring-2 ring-rose/40" />
                <div className="flex-1">
                  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-terracotta">Stop {index + 1} of {ROOMS.length} · {ROOMS[index].title}</p>
                  <p key={index} className="mt-1.5 animate-fade-up font-serif text-lg italic leading-relaxed text-charcoal" data-testid="tour-note">“{ROOMS[index].note}”</p>
                  <p className="mt-1 text-xs text-taupe">— {CLINIC.doctorName}</p>
                </div>
                <button onClick={endTour} aria-label="End tour" data-testid="tour-end" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-soft-border text-taupe hover:text-terracotta"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 flex gap-1.5" aria-hidden="true">
                {ROOMS.map((r, i) => (
                  <span key={r.src} className="h-1 flex-1 overflow-hidden rounded-full bg-terracotta/15">
                    <span key={`${i}-${index}`} className="block h-full rounded-full bg-terracotta" style={i < index ? { width: "100%" } : i === index ? { animation: `tour-fill ${TOUR_STEP_MS}ms linear forwards` } : { width: 0 }} />
                  </span>
                ))}
              </div>
              <style>{`@keyframes tour-fill { from { width: 0 } to { width: 100% } }`}</style>
            </div>
          )}
          <div className="mt-8 flex justify-center gap-3" data-testid="digital-visit-thumbs">
            {ROOMS.map((r, i) => (
              <button key={r.src} onClick={() => setIndex(i)} aria-label={`View ${r.title}`} data-testid={`digital-visit-thumb-${i}`} className={`overflow-hidden rounded-xl ring-2 transition-[transform,opacity] duration-300 ${i === index ? "scale-105 ring-terracotta opacity-100" : "ring-transparent opacity-60 hover:opacity-100"}`}>
                <img src={r.src} alt="" className="h-12 w-20 object-cover md:h-16 md:w-28" />
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => (
            <Reveal key={p.t} delay={i * 80}>
              <div className="h-full rounded-3xl border border-soft-border bg-cream/80 p-7 text-center backdrop-blur transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-card">
                <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-terracotta text-cream"><p.icon className="h-6 w-6" /></span>
                <h3 className="mt-5 font-sans text-xs font-bold uppercase tracking-[0.15em] text-terracotta-dark">{p.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-taupe">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {!compact && (
          <Reveal delay={100} className="mt-12 grid gap-4 md:grid-cols-3">
            <div data-testid="now-welcoming-badge" className="rounded-3xl border border-gold/30 bg-cream p-5 shadow-soft">
              <div className="flex items-center gap-3">
                <span className="relative inline-flex h-3 w-3">
                  {status.open && <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/60" />}
                  <span className={`relative h-3 w-3 rounded-full ${status.open ? "bg-emerald-500" : "bg-taupe/50"}`} />
                </span>
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Now Welcoming Patients</p>
              </div>
              <p data-testid="open-status-label" className={`mt-3 font-serif text-2xl ${status.open ? "text-emerald-700" : "text-charcoal"}`}>{status.label}</p>
              <p data-testid="open-status-detail" className="mt-1 text-sm font-medium text-taupe">{status.detail} <span className="text-xs font-normal">(IST)</span></p>
              <p className="mt-3 text-sm leading-relaxed text-taupe">Appointments help us keep your wait short — call or book online.</p>
              <a href={tel(CLINIC.appointmentMobile)} data-testid="digital-visit-call" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-terracotta link-underline"><Phone className="h-4 w-4" /> {CLINIC.appointmentMobile}</a>
            </div>
            <div data-testid="clinic-timings-card" className="rounded-3xl border border-gold/30 bg-cream p-5 shadow-soft">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-terracotta text-cream"><Clock className="h-4 w-4" /></span>
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Clinic Timings</p>
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {CLINIC.timingsList.map((t) => (
                  <li key={t.day} className="flex justify-between gap-3 border-b border-soft-border pb-2 last:border-none last:pb-0">
                    <span className="text-taupe">{t.day}</span><span className="font-medium text-charcoal">{t.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="get-directions-card" className="flex flex-col rounded-3xl border border-gold/30 bg-cream p-5 shadow-soft">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-terracotta text-cream"><MapPin className="h-4 w-4" /></span>
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Find Us</p>
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-taupe">{CLINIC.address}</p>
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="get-directions-btn" className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3 font-sans text-sm font-semibold text-cream transition-colors hover:bg-terracotta-dark">
                <Navigation className="h-4 w-4" /> Get Directions
              </a>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
